/* servisRadar.js — JEDEN ZDROJ PRAVDY pro servisní radar.
 *
 * Používá /crm/servis-radar (ServisRadarPage), nástěnkový blok i notifikační
 * centrum (zvoneček) — ať se „za kolik dní" a co je servis / výročí / narozeniny
 * nepočítá dvakrát jinak. Kdo přidá signál, přidá ho SEM (a ukáže se všude).
 *
 * Prahy (dny): smlouvy (fixace/dožití/výročí) 120, výročí klienta 120,
 *              příští servis 120, narozeniny 30 — stejné jako původní radar.
 */

// Kategorizace typů smluv (readable štítek do titulku položky).
export const CT_LABEL = {
  cs: { hypoteka: "Hypotéka", pojisteni_auta: "Pojištění auta", pojisteni_zivot: "Životní pojištění", pojisteni_majetek: "Pojištění majetku", penzijko: "Penzijní", investice: "Investice", uver: "Úvěr", stavebni: "Stavební spoření", zdravotni: "Zdravotní", jine: "Smlouva", pojisteni_odpovednost: "Pojištění odpovědnosti", pojisteni_ostatni: "Ostatní pojištění" },
  sk: { hypoteka: "Hypotéka", pojisteni_auta: "Poistenie auta", pojisteni_zivot: "Životné poistenie", pojisteni_majetek: "Poistenie majetku", penzijko: "Penzijné", investice: "Investície", uver: "Úver", stavebni: "Stavebné sporenie", zdravotni: "Zdravotné", jine: "Zmluva", pojisteni_odpovednost: "Poistenie zodpovednosti", pojisteni_ostatni: "Ostatné poistenie" },
};

// Štítek dle druhu signálu (CS/SK) — bez detailu smlouvy. Bez emoji: ikonu
// (dort u narozenin) kreslí obrazovka sama — Servisní radar i zvoneček.
export const KIND_LABEL = {
  cs: { refix: "Konec fixace", konec: "Dožití / konec", vyroci: "Výročí smlouvy", servis: "Příští servis", vyroci_klient: "Výročí klienta", narozeniny: "Narozeniny" },
  sk: { refix: "Koniec fixácie", konec: "Dožitie / koniec", vyroci: "Výročie zmluvy", servis: "Ďalší servis", vyroci_klient: "Výročie klienta", narozeniny: "Narodeniny" },
};

export const RADAR_FIX_WINDOW = 120;    // smlouvy: konec fixace / dožití / výročí smlouvy
export const RADAR_ANNIV_WINDOW = 120;  // výročí klienta (contact.anniversary)
export const RADAR_SERVIS_WINDOW = 120; // ruční příští servis (contact.servis_next_date)
export const RADAR_BDAY_WINDOW = 30;    // narozeniny klienta (contact.dob)

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

// Počet celých dní od dneška do zadaného ISO data (záporné = po termínu).
export const dnyDo = (iso) => {
  try {
    const d = startOfDay(new Date(iso));
    const t = startOfDay(new Date());
    if (isNaN(d.getTime())) return 9999;
    return Math.round((d - t) / 86400000);
  } catch { return 9999; }
};

// Nejbližší budoucí výskyt opakovaného data (narozeniny / výročí) — bere MĚSÍC+DEN, ne rok.
export const nextAnnual = (iso) => {
  try {
    const b = new Date(iso);
    if (isNaN(b.getTime())) return null;
    const t = startOfDay(new Date());
    let n = new Date(t.getFullYear(), b.getMonth(), b.getDate());
    if (n < t) n = new Date(t.getFullYear() + 1, b.getMonth(), b.getDate());
    return n;
  } catch { return null; }
};

const jmenoContact = (c) => [c?.name, c?.surname].filter(Boolean).join(" ").trim() || c?.company || "Klient";
const jmenoFix = (r) => `${r?.contact_name || ""} ${r?.contact_surname || ""}`.trim() || "Klient";

/* computeServisRadar — sloučí smluvní signály (konec fixace / dožití / výročí smlouvy)
 * s klientskými (příští servis, výročí klienta, narozeniny) do JEDNOHO seřazeného seznamu.
 *
 * Vrací položky: { id, kind, icon, date (ISO), days, contact_id, name, phone, title,
 *                  typ, produkt, instituce }.
 * kind: refix | konec | vyroci | servis | vyroci_klient | narozeniny
 *
 * typ/produkt/instituce nesou KONKRÉTNÍ smlouvu (surový contract_type, čitelný
 * název, banka/pojišťovna) — servisní zpráva na WhatsApp musí říct, o kterou
 * smlouvu jde, ne obecně „vaše smlouvy". U klientských signálů (servis, výročí
 * klienta, narozeniny) žádná smlouva není → zůstávají prázdné.
 */
export function computeServisRadar({
  fixations = [],
  contacts = [],
  lang = "cs",
  fixWindow = RADAR_FIX_WINDOW,
  annivWindow = RADAR_ANNIV_WINDOW,
  servisWindow = RADAR_SERVIS_WINDOW,
  bdayWindow = RADAR_BDAY_WINDOW,
} = {}) {
  const L = KIND_LABEL[lang === "sk" ? "sk" : "cs"];
  const CT = CT_LABEL[lang === "sk" ? "sk" : "cs"];
  const out = [];

  // 1) Smlouvy — konec fixace / dožití / výročí smlouvy (z /api/contracts/upcoming-fixations)
  (fixations || []).forEach((r) => {
    if (!r) return;
    const prov = r.provider ? ` · ${r.provider}` : "";
    const ctl = CT[r.contract_type] || CT.jine;
    const base = {
      contact_id: r.contact_id, name: jmenoFix(r), phone: r.contact_phone,
      typ: r.contract_type || "", produkt: ctl, instituce: r.provider || "",
    };
    if (r.fixation_end && dnyDo(r.fixation_end) >= 0 && dnyDo(r.fixation_end) <= fixWindow) {
      out.push({ ...base, id: `${r.id}-refix`, kind: "refix", icon: "refix", date: r.fixation_end, days: dnyDo(r.fixation_end), title: `${L.refix}: ${ctl}${prov}` });
    } else if (r.end_date && dnyDo(r.end_date) >= 0 && dnyDo(r.end_date) <= fixWindow) {
      out.push({ ...base, id: `${r.id}-konec`, kind: "konec", icon: "refix", date: r.end_date, days: dnyDo(r.end_date), title: `${L.konec}: ${ctl}${prov}` });
    } else if (r.anniversary_date && dnyDo(r.anniversary_date) >= 0 && dnyDo(r.anniversary_date) <= fixWindow) {
      out.push({ ...base, id: `${r.id}-vyroci`, kind: "vyroci", icon: "vyroci", date: r.anniversary_date, days: dnyDo(r.anniversary_date), title: `${L.vyroci}: ${ctl}${prov}` });
    }
  });

  // 2) Kontakty — příští servis (ruční datum) + výročí klienta + narozeniny
  (contacts || []).forEach((c) => {
    if (!c || !c.id) return;
    // Klientské signály nemají smlouvu — typ/produkt/instituce zůstávají prázdné.
    const base = { contact_id: c.id, name: jmenoContact(c), phone: c.phone, typ: "", produkt: "", instituce: "" };
    // Příští servis — jednorázové datum na kartě klienta
    if (c.servis_next_date) {
      const d = dnyDo(c.servis_next_date);
      if (d >= 0 && d <= servisWindow) out.push({ ...base, id: `servis-${c.id}`, kind: "servis", icon: "servis", date: c.servis_next_date, days: d, title: L.servis });
    }
    // Výročí klienta — opakované (MĚSÍC+DEN)
    if (c.anniversary) {
      const na = nextAnnual(c.anniversary);
      if (na) {
        const d = dnyDo(na.toISOString());
        if (d >= 0 && d <= annivWindow) out.push({ ...base, id: `anniv-${c.id}`, kind: "vyroci_klient", icon: "vyroci", date: na.toISOString(), days: d, title: L.vyroci_klient });
      }
    }
    // Narozeniny — opakované (MĚSÍC+DEN)
    const dob = c.dob || c.narozeni;
    if (dob) {
      const nb = nextAnnual(dob);
      if (nb) {
        const d = dnyDo(nb.toISOString());
        if (d >= 0 && d <= bdayWindow) out.push({ ...base, id: `bday-${c.id}`, kind: "narozeniny", icon: "bday", date: nb.toISOString(), days: d, title: L.narozeniny });
      }
    }
  });

  return out.sort((a, b) => a.days - b.days);
}
